import { Checkbox as MuiCheckbox } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import {
  CheckboxIconProps,
  CheckboxProps,
  CheckboxVariantEnum,
  LabelProps
} from './types';

const CustomIcon = styled('div', {
  shouldForwardProp: (prop) => prop !== 'borderColor'
})<CheckboxIconProps>(({ theme, borderColor }) => ({
  // styles from Bento Figma file https://www.figma.com/file/RGWZ0GKYaE7OKgMDW6FGgu/%F0%9F%8C%90%5BBento%5D-Web?node-id=132%3A29
  borderRadius: theme.space.xxs,
  border: `1.5px solid ${borderColor}`,
  width: 15,
  height: 15,
  'input:hover ~ &': {
    border: `1.5px solid ${theme.palette.interaction.primaryHover}`
  },
  'input:disabled ~ &': {
    border: `1.5px solid ${theme.palette.neutral.inactive}`
  }
}));

const CheckedIcon = styled(CustomIcon)(({ theme }) => ({
  backgroundColor: theme.palette.interaction.primary,
  'input:hover ~ &': {
    backgroundColor: theme.palette.interaction.primaryHover
  },
  'input:disabled ~ &': {
    backgroundColor: theme.palette.neutral.inactive
  },
  '&:before': {
    display: 'block',
    width: 15,
    height: 15,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '50% 50%',
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' class='checkmark' width='11' height='15' xmlns='http://www.w3.org/2000/svg' " +
      "viewBox='0 0 11 8'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8.7508 0.533146C9.04369 0.240253 9.51856 0.240253 9.81146 0.533146C10.0777 " +
      '0.799413 10.1019 1.21608 9.88408 1.50969L9.81146 1.59381L3.65613 7.74914L0.219548 4.31256C-0.0733454 4.01966 -0.0733454 3.54479 0.219548 3.2519C0.485814 ' +
      "2.98563 0.902478 2.96142 1.19609 3.17928L1.28021 3.2519L3.656 5.6271L8.7508 0.533146Z' fill='%23ffffff'%3E%3C/path%3E%3C/svg%3E\")",
    content: '""'
  }
}));

const LabelContainer = styled('label')<LabelProps>(({ theme, disabled }) => ({
  color: disabled
    ? theme.palette.neutral.inactive
    : theme.palette.neutral.primary,
  '&:hover': {
    color: disabled
      ? theme.palette.neutral.inactive
      : theme.palette.interaction.primaryHover
  }
}));

const TextContainer = styled('div')(({ theme }) => ({
  marginLeft: theme.space.sm,
  ...theme.typography.labelMd
}));

export const Checkbox = (props: CheckboxProps) => {
  const theme = useTheme();
  const { label, disabled = false, variant = 'primary' } = props;

  //   checkbox border color variants
  const checkedBorderColor = theme.palette.interaction.primary;
  let uncheckedBorderColor = theme.palette.interaction.primary;
  switch (variant) {
    case CheckboxVariantEnum.primary:
      uncheckedBorderColor = theme.palette.interaction.primary;
      break;
    case CheckboxVariantEnum.secondary:
      uncheckedBorderColor = theme.palette.interaction.secondary;
      break;
  }

  const CheckboxComponent = (
    <MuiCheckbox
      disableRipple
      checkedIcon={<CheckedIcon borderColor={checkedBorderColor} />}
      icon={<CustomIcon borderColor={uncheckedBorderColor} />}
      {...props}
    />
  );

  if (label) {
    return (
      <LabelContainer
        style={{ display: 'inline-flex', alignItems: 'center' }}
        disabled={disabled}
      >
        {CheckboxComponent}
        <TextContainer>{label}</TextContainer>
      </LabelContainer>
    );
  } else {
    return CheckboxComponent;
  }
};
